<template>
  <div
    class="wrapper container"
    id="app"
    :style="{ marginLeft: isMobile ? '21px' : '' }"
  >
    <Layout :user="user"></Layout>

    <div class="container py-4">
      <!-- Dashboard Header -->
      <div class="row mb-4 text-center">
        <div class="col-12">
          <h1 class="fw-bold text-brand-dark">Athlete Dashboard</h1>
          <p class="text-muted">
            Showcase your athletic talent and track your progress
          </p>
        </div>
      </div>

      <div class="row">
        <!-- Athlete Profile Card -->
        <div class="col-md-6 mb-4">
          <div class="card border-0 shadow h-100">
            <div
              class="card-header bg-brand-green"
              style="height: 8px; padding: 0"
            ></div>
            <div class="card-body p-4">
              <div
                class="d-flex justify-content-between align-items-center mb-4"
              >
                <h2 class="card-title fw-bold mb-0 text-brand-purple">
                  My Athlete Profile
                </h2>

                <!-- Show status badge only if they have a website -->
                <span
                  v-if="hasAthleteWebsite"
                  class="badge bg-brand-green text-white rounded-pill px-3"
                  >Active</span
                >
                <span
                  v-else
                  class="badge bg-warning text-dark rounded-pill px-3"
                  >Not Created</span
                >
              </div>

              <!-- If they have a website, show QR code and sharing options -->
              <div
                v-if="hasAthleteWebsite"
                class="d-flex flex-column align-items-center my-4"
              >
                <div class="bg-white p-3 shadow-sm rounded mb-3">
                  <qrcode-vue
                    :value="getQRLink"
                    :size="getQRSize"
                    level="H"
                    class="rounded"
                  />
                </div>

                <a
                  :href="getQRLink"
                  target="_blank"
                  class="text-brand-green fw-medium d-flex align-items-center gap-2"
                >
                  <span>View Athlete Profile</span>
                  <i class="fas fa-external-link-alt"></i>
                </a>

                <hr class="w-100 my-4" />

                <div class="mt-3">
                  <h3
                    class="fs-5 fw-semibold mb-3 text-center text-brand-purple"
                  >
                    Share Your Talent!
                  </h3>

                  <div class="d-flex justify-content-center gap-3">
                    <a
                      :href="getTwitterShareLink"
                      target="_blank"
                      class="btn btn-outline-brand-purple rounded-circle d-flex align-items-center justify-content-center"
                      style="width: 40px; height: 40px"
                    >
                      <i class="fa-brands fa-square-x-twitter"></i>
                    </a>
                    <a
                      :href="getFacebookShareLink"
                      target="_blank"
                      class="btn btn-outline-brand-purple rounded-circle d-flex align-items-center justify-content-center"
                      style="width: 40px; height: 40px"
                    >
                      <i class="fa-brands fa-square-facebook"></i>
                    </a>
                  </div>
                </div>
              </div>

              <!-- If they don't have a website, show create profile prompt -->
              <div v-else class="text-center py-4">
                <div class="mb-4">
                  <div
                    class="bg-brand-purple-light rounded-circle mx-auto d-flex align-items-center justify-content-center"
                    style="width: 80px; height: 80px"
                  >
                    <i
                      class="fas fa-user-plus text-brand-purple"
                      style="font-size: 32px"
                    ></i>
                  </div>
                </div>

                <h3 class="fs-5 fw-semibold mb-3 text-brand-purple">
                  Create Your Athlete Profile
                </h3>
                <p class="text-muted mb-4">
                  Showcase your athletic achievements and get discovered by
                  coaches and recruiters.
                </p>

                <button
                  class="btn btn-brand-green text-white px-4 py-2 rounded-pill"
                  @click="createNewProfile"
                >
                  <i class="fas fa-plus-circle me-2"></i>
                  Create Profile
                </button>

                <div class="mt-4 pt-3 border-top">
                  <div class="d-flex align-items-center justify-content-center">
                    <div class="bg-light rounded-circle p-2 me-2">
                      <i class="fas fa-info-circle text-brand-purple"></i>
                    </div>
                    <p class="text-muted small mb-0">
                      Your profile will be visible to coaches and recruiters
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Profile Stats Card -->
        <div class="col-md-6 mb-4">
          <div class="card border-0 shadow h-100">
            <div
              class="card-header bg-brand-purple"
              style="height: 8px; padding: 0"
            ></div>
            <div class="card-body p-4">
              <div
                class="d-flex justify-content-between align-items-center mb-4"
              >
                <h2 class="card-title fw-bold mb-0 text-brand-purple">
                  Profile Analytics
                </h2>
                <span class="badge bg-brand-purple text-white rounded-pill px-3"
                  >Insights</span
                >
              </div>

              <!-- If they have a website, show analytics -->
              <div v-if="hasAthleteWebsite" class="row g-4">
                <!-- Total Views -->
                <div class="col-12">
                  <div class="card bg-light border-0 p-3">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <p class="text-muted mb-1 small">Total Profile Views</p>
                        <h3 class="fs-2 fw-bold mb-0 text-brand-dark">
                          {{ getTotalViews }}
                        </h3>
                      </div>
                      <div class="bg-brand-purple-light rounded-circle p-3">
                        <i class="fas fa-eye text-brand-purple"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Monthly Views -->
                <div class="col-12">
                  <div class="card bg-light border-0 p-3">
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <div>
                        <p class="text-muted mb-1 small">Views This Month</p>
                        <h3 class="fs-2 fw-bold mb-0 text-brand-dark">TBD</h3>
                      </div>
                      <div class="bg-brand-green-light rounded-circle p-3">
                        <i class="fas fa-chart-line text-brand-green"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- If they don't have a website, show benefits -->
              <div v-else class="py-3">
                <h3 class="fs-5 fw-semibold mb-3 text-brand-purple">
                  Benefits of Your Athlete Profile
                </h3>

                <div class="mb-3 p-3 bg-light rounded">
                  <div class="d-flex">
                    <div
                      class="bg-brand-green-light rounded-circle p-2 me-3 flex-shrink-0"
                    >
                      <i class="fas fa-search text-brand-green"></i>
                    </div>
                    <div>
                      <h4 class="fs-6 fw-semibold text-brand-dark">
                        Get Discovered
                      </h4>
                      <p class="text-muted small mb-0">
                        Coaches and recruiters can see your profile and contact
                        you directly.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="mb-3 p-3 bg-light rounded">
                  <div class="d-flex">
                    <div
                      class="bg-brand-purple-light rounded-circle p-2 me-3 flex-shrink-0"
                    >
                      <i class="fas fa-trophy text-brand-purple"></i>
                    </div>
                    <div>
                      <h4 class="fs-6 fw-semibold text-brand-dark">
                        Showcase Achievements
                      </h4>
                      <p class="text-muted small mb-0">
                        Display your stats, awards, and athletic
                        accomplishments.
                      </p>
                    </div>
                  </div>
                </div>

                <div class="mb-3 p-3 bg-light rounded">
                  <div class="d-flex">
                    <div
                      class="bg-brand-green-light rounded-circle p-2 me-3 flex-shrink-0"
                    >
                      <i class="fas fa-share-alt text-brand-green"></i>
                    </div>
                    <div>
                      <h4 class="fs-6 fw-semibold text-brand-dark">
                        Easy Sharing
                      </h4>
                      <p class="text-muted small mb-0">
                        Share your profile with coaches, teams, and on social
                        media.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Coming Soon Section -->
              <div class="mt-4 pt-3 border-top">
                <div class="d-flex align-items-center">
                  <span
                    class="badge bg-brand-green text-white rounded-pill px-3 me-2"
                    >Coming Soon</span
                  >
                  <p class="text-muted small mb-0">
                    Advanced analytics and training materials
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Actions Section -->
      <div class="row">
        <div class="col-12 mb-4">
          <div class="card border-0 shadow">
            <div class="card-body p-4">
              <h2 class="fs-4 fw-bold mb-4 text-brand-purple">Quick Actions</h2>

              <div class="row g-3">
                <div class="col-md-4">
                  <button
                    class="btn btn-light text-start p-3 w-100 h-100 d-flex align-items-center action-btn"
                    @click="updateProfile"
                  >
                    <div class="bg-brand-purple-light rounded-circle p-2 me-3">
                      <i class="fas fa-edit text-brand-purple"></i>
                    </div>
                    <div>
                      <p class="fw-medium mb-0 text-brand-dark">
                        Update Profile
                      </p>
                      <p class="small text-muted mb-0">
                        Edit your athlete information
                      </p>
                    </div>
                  </button>
                </div>

                <div class="col-md-4">
                  <button
                    class="btn btn-light text-start p-3 w-100 h-100 d-flex align-items-center action-btn"
                    @click="viewActivities"
                  >
                    <div class="bg-brand-green-light rounded-circle p-2 me-3">
                      <i class="fas fa-calendar text-brand-green"></i>
                    </div>
                    <div>
                      <p class="fw-medium mb-0 text-brand-dark">
                        View Activities
                      </p>
                      <p class="small text-muted mb-0">Camps and memberships</p>
                    </div>
                  </button>
                </div>

                <div class="col-md-4">
                  <button
                    class="btn btn-light text-start p-3 w-100 h-100 d-flex align-items-center action-btn"
                    @click="viewMessages"
                  >
                    <div class="bg-brand-purple-light rounded-circle p-2 me-3">
                      <i class="fas fa-comments text-brand-purple"></i>
                    </div>
                    <div>
                      <p class="fw-medium mb-0 text-brand-dark">Messages</p>
                      <p class="small text-muted mb-0">Contact coaches</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from '../components/Layout.vue'
import QrcodeVue from 'qrcode.vue'
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  components: {
    Layout,
    QrcodeVue,
  },
  data() {
    return {
      value: 'https://example.com',
      websites: [],
      size: 150,
      user: {},
      totalCash: null,
      payoutInfo: {
        payoutTotal: 0,
      },
      accountBalance: 0,
      deletedCamps: [],
      deletedMemberships: [],
      totalCampers: null,
      tshirts: {
        youthSmall: null,
        youthMedium: null,
        youthLarge: null,
        adultSmall: null,
        adultMedium: null,
        adultLarge: null,
        adultXLarge: null,
        adultXXLarge: null,
      },
      camps: [],
      teamCamps: [],
      memberships: [],
    }
  },
  methods: {
    getUserDetails() {
      this.user = JSON.parse(localStorage.getItem('user'))
    },

    getAIWebsites() {
      let url =
        import.meta.env.VITE_APP_DB_URL +
        'recruit/getRecruitWebsites?id=' +
        this.user._id
      axios.get(url).then(response => {
        this.websites = response.data.aiWebsites
      })
    },

    shareTwitter() {},

    createNewProfile() {
      if (!this.user.recruitProfile.athleticInfo?.primarySport) {
        Swal.fire({
          icon: 'warning',
          title: 'Incomplete Profile',
          text: 'Please fill out the athletic bio information first.',
        }).then(() => {
          this.$router.push('/Recruit/Dashboard/Profile?createWebsite=true')
        })
      } else {
        this.$router.push('/Recruit/Dashboard/Website')
      }
    },

    updateProfile() {
      this.$router.push('/Recruit/Dashboard/Profile')
    },

    viewActivities() {
      this.$router.push('/Recruit/Dashboard/Activities')
    },

    viewMessages() {
      this.$router.push('/Recruit/Dashboard/Inbox')
    },
  },
  created() {
    this.getUserDetails()
    this.getAIWebsites()
  },

  mounted() {},

  computed: {
    hasAthleteWebsite() {
      return (
        (this.user.aiWebsites && this.user.aiWebsites.length > 0) ||
        this.websites.length > 0
      )
    },

    isMobile: function () {
      var check = false
      ;(function (a) {
        if (
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
            a,
          ) ||
          /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
            a.substr(0, 4),
          )
        )
          check = true
      })(navigator.userAgent || navigator.vendor || window.opera)
      return check
    },
    getWebsiteLink() {
      return '/coach/' + this.user._id
    },

    getQRLink() {
      if (this.user.aiWebsites && this.user.aiWebsites.length > 0) {
        const subdomain = this.user.aiWebsites[0].subdomain
        return 'https://' + subdomain + '.summerathletes.com'
      } else if (this.websites.length > 0) {
        return 'https://' + this.websites[0].subdomain + '.summerathletes.com'
      } else {
        return 'https://summerathletes.com'
      }
    },

    getTotalViews() {
      return this.user.analytics && this.user.analytics.totalViews
        ? this.user.analytics.totalViews
        : 'TBD'
    },

    getQRSize() {
      return '200'
    },

    getTwitterShareLink() {
      let link = 'https://twitter.com/intent/tweet?text='
      let text =
        "Check%20out%20this%20new%20athlete%20profile!%20It's%20the%20best%20way%20to%20showcase%20your%20skills%20to%20scouts%20and%20recruiters! \n" +
        this.getQRLink
      return link + text
    },

    getFacebookShareLink() {
      let link = 'https://www.facebook.com/sharer/sharer.php?u='
      let text = this.getQRLink
      return link + text
    },
  },
}
</script>

<style scoped>
/* Brand Colors */
:root {
  --brand-green: #19d412;
  --brand-purple: #363166;
  --brand-dark: #030021;
  --brand-green-light: rgba(25, 212, 18, 0.15);
  --brand-purple-light: rgba(54, 49, 102, 0.15);
}

/* Text Colors */
.text-brand-green {
  color: #19d412 !important;
}

.text-brand-purple {
  color: #363166 !important;
}

.text-brand-dark {
  color: #030021 !important;
}

/* Background Colors */
.bg-brand-green {
  background-color: #19d412 !important;
}

.bg-brand-purple {
  background-color: #363166 !important;
}

.bg-brand-dark {
  background-color: #030021 !important;
}

.bg-brand-green-light {
  background-color: rgba(25, 212, 18, 0.15) !important;
}

.bg-brand-purple-light {
  background-color: rgba(54, 49, 102, 0.15) !important;
}

/* Button Styles */
.btn-outline-brand-purple {
  color: #363166;
  border-color: #363166;
}

.btn-outline-brand-purple:hover {
  color: white;
  background-color: #363166;
  border-color: #363166;
}

.btn-brand-green {
  background-color: #19d412;
  border-color: #19d412;
}

.btn-brand-green:hover {
  background-color: #16b910;
  border-color: #16b910;
}

/* Card Styles */
.card {
  transition: all 0.3s ease;
  border-radius: 0.5rem;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.shadow {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
}

.shadow-sm {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

/* Action Button Hover Effect */
.action-btn {
  transition: all 0.2s ease;
  border-radius: 0.5rem;
}

.action-btn:hover {
  background-color: rgba(54, 49, 102, 0.05) !important;
}

/* Utility Classes */
.fw-medium {
  font-weight: 500;
}

.gap-2 {
  gap: 0.5rem;
}

.gap-3 {
  gap: 1rem;
}

.gap-4 {
  gap: 1.5rem;
}

.rounded-pill {
  border-radius: 50rem !important;
}
</style>
